<template>
  <div>
    <!-- Full-screen loading overlay -->
    <v-overlay v-model="pageLoading" class="align-center justify-center" persistent>
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <div class="mt-4 text-h6">Đang tải dữ liệu...</div>
      </div>
    </v-overlay>

    <!-- Header -->
    <v-card class="mb-6">
      <v-card-title>
        <v-icon class="mr-2">mdi-comment-multiple</v-icon>
        Quản lý Feedback
        <v-spacer />
        <v-btn color="primary" @click="loadFeedback">
          <v-icon left>mdi-refresh</v-icon>
          Làm mới
        </v-btn>
      </v-card-title>
    </v-card>

    <!-- Feedback Table -->
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-table</v-icon>
        Danh sách Feedback
        <v-spacer />
        <v-text-field
          v-model="search"
          label="Tìm kiếm feedback..."
          prepend-inner-icon="mdi-magnify"
          clearable
          @input="debouncedSearch"
          style="max-width: 300px;"
        />
      </v-card-title>
      
      <v-data-table
        :headers="headers"
        :items="feedbackList"
        :loading="loading"
        :items-per-page="pagination.limit"
        :page="pagination.page"
        :server-items-length="pagination.total"
        @update:page="onPageChange"
        @update:items-per-page="onItemsPerPageChange"
        class="elevation-1"
        :search="search"
      >
        <!-- ID Column -->
        <template v-slot:item.id="{ item }">
          <v-chip color="primary" size="small" variant="outlined">
            #{{ item.id }}
          </v-chip>
        </template>

        <!-- Content Column -->
        <template v-slot:item.content="{ item }">
          <div class="feedback-content">
            <p class="mb-0 text-body-2">{{ item.content }}</p>
          </div>
        </template>

        <!-- Created At Column -->
        <template v-slot:item.created_at="{ item }">
          <div class="d-flex flex-column">
            <span class="text-body-2">{{ formatDate(item.created_at) }}</span>
            <span class="text-caption text-grey">{{ formatTime(item.created_at) }}</span>
          </div>
        </template>

        <!-- Actions Column -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            icon="mdi-eye"
            size="small"
            color="info"
            @click="viewFeedback(item)"
            title="Xem chi tiết"
          />
          <v-btn
            icon="mdi-delete"
            size="small"
            color="error"
            @click="confirmDelete(item)"
            title="Xóa feedback"
          />
        </template>

        <!-- No data template -->
        <template v-slot:no-data>
          <div class="text-center py-8">
            <v-icon size="64" color="grey-lighten-1">mdi-comment-off</v-icon>
            <div class="text-h6 text-grey mt-4">Chưa có feedback nào</div>
            <div class="text-body-2 text-grey">Khách hàng chưa để lại phản hồi nào</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- View Feedback Dialog -->
    <v-dialog v-model="viewDialog" max-width="600">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-eye</v-icon>
          Chi tiết Feedback #{{ selectedFeedback?.id }}
        </v-card-title>
        <v-card-text>
          <div v-if="selectedFeedback">
            <div class="mb-4">
              <h4 class="text-h6 mb-2">Nội dung:</h4>
              <v-card variant="outlined" class="pa-4">
                <p class="text-body-1 mb-0">{{ selectedFeedback.content }}</p>
              </v-card>
            </div>
            
            <div class="mb-4">
              <h4 class="text-h6 mb-2">Thông tin:</h4>
              <v-list density="compact">
                <v-list-item>
                  <v-list-item-title>ID Feedback</v-list-item-title>
                  <v-list-item-subtitle>#{{ selectedFeedback.id }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Ngày tạo</v-list-item-title>
                  <v-list-item-subtitle>{{ formatDateTime(selectedFeedback.created_at) }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="viewDialog = false">Đóng</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h5">Xác nhận xóa</v-card-title>
        <v-card-text>
          Bạn có chắc chắn muốn xóa feedback #{{ selectedFeedback?.id }}?
          <div class="mt-3 pa-3 bg-grey-lighten-5 rounded">
            <p class="text-body-2 mb-0">{{ selectedFeedback?.content }}</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="deleteDialog = false">Hủy</v-btn>
          <v-btn
            color="error"
            @click="deleteFeedback"
            :loading="deleting"
          >
            Xóa
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import feedbackService from '@/services/feedbackService';

export default {
  name: 'Feedback',
  data() {
    return {
      // Loading states
      pageLoading: false,
      loading: false,
      deleting: false,

      // Data
      feedbackList: [],
      search: '',

      // Pagination
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      },

      // Table headers
      headers: [
        { title: 'ID', key: 'id', sortable: false, width: '100px' },
        { title: 'Nội dung', key: 'content', sortable: false },
        { title: 'Ngày tạo', key: 'created_at', sortable: false, width: '200px' },
        { title: 'Thao tác', key: 'actions', sortable: false, width: '120px' }
      ],

      // Dialogs
      viewDialog: false,
      deleteDialog: false,
      selectedFeedback: null,

      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },

      // Search debounce
      searchTimeout: null
    };
  },

  async mounted() {
    await this.loadFeedback();
  },

  methods: {
    async loadFeedback() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit
        };

        const response = await feedbackService.getFeedbackList(params);
        
        if (response.data.success) {
          this.feedbackList = response.data.data || [];
          this.pagination.total = response.data.pagination?.total || 0;
          this.pagination.totalPages = response.data.pagination?.totalPages || 0;
        } else if (Array.isArray(response.data)) {
          // Fallback nếu API trả về array trực tiếp
          this.feedbackList = response.data;
          this.pagination.total = response.data.length;
          this.pagination.totalPages = 1;
        }
      } catch (error) {
        this.showSnackbar('Lỗi khi tải danh sách feedback', 'error');
        console.error('Error loading feedback:', error);
      } finally {
        this.loading = false;
      }
    },

    viewFeedback(item) {
      this.selectedFeedback = item;
      this.viewDialog = true;
    },

    confirmDelete(item) {
      this.selectedFeedback = item;
      this.deleteDialog = true;
    },

    async deleteFeedback() {
      this.deleting = true;
      try {
        // Note: API delete không có trong tài liệu, có thể cần implement
        // await feedbackService.deleteFeedback(this.selectedFeedback.id);
        
        // Tạm thời xóa khỏi danh sách local
        this.feedbackList = this.feedbackList.filter(
          item => item.id !== this.selectedFeedback.id
        );
        
        this.showSnackbar('Xóa feedback thành công', 'success');
        this.deleteDialog = false;
      } catch (error) {
        this.showSnackbar('Lỗi khi xóa feedback', 'error');
        console.error('Error deleting feedback:', error);
      } finally {
        this.deleting = false;
      }
    },

    onPageChange(page) {
      this.pagination.page = page;
      this.loadFeedback();
    },

    onItemsPerPageChange(itemsPerPage) {
      this.pagination.limit = itemsPerPage;
      this.pagination.page = 1;
      this.loadFeedback();
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.loadFeedback();
      }, 500);
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },

    formatTime(dateString) {
      return new Date(dateString).toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateTime(dateString) {
      return new Date(dateString).toLocaleString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color
      };
    }
  }
};
</script>

<style scoped>
.feedback-content {
  max-width: 400px;
  word-wrap: break-word;
}

.v-data-table {
  border-radius: 8px;
}

.v-card {
  border-radius: 12px;
}
</style>
